interface AccordionAnimationState {
  animation: Animation | null;
  isClosing: boolean;
  isExpanding: boolean;
}

const ACCORDION_SUMMARY_SELECTOR =
  ".accordion > details > summary, details[data-animated-accordion] > summary";
const DURATION = 320;
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

const accordionStates = new WeakMap<HTMLDetailsElement, AccordionAnimationState>();

function getState(details: HTMLDetailsElement): AccordionAnimationState {
  let state = accordionStates.get(details);
  if (!state) {
    state = {
      animation: null,
      isClosing: false,
      isExpanding: false,
    };
    accordionStates.set(details, state);
  }
  return state;
}

function isReducedMotionPreferred(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getElementHeight(element: Element): number {
  return element.getBoundingClientRect().height;
}

function clearAnimatedStyles(details: HTMLDetailsElement): void {
  details.style.height = "";
  details.style.overflow = "";
}

function cancelOngoingAnimation(details: HTMLDetailsElement): void {
  const state = getState(details);
  if (!state.animation) return;

  // Freeze the current visual height before cancel to avoid stale height restores on rapid clicks.
  details.style.height = `${getElementHeight(details)}px`;
  details.style.overflow = "hidden";

  state.animation.onfinish = null;
  state.animation.oncancel = null;
  state.animation.cancel();
  state.animation = null;
}

function resetAnimationFlags(details: HTMLDetailsElement): void {
  const state = getState(details);
  state.isClosing = false;
  state.isExpanding = false;
}

function animateAccordionHeight(
  details: HTMLDetailsElement,
  startHeight: number,
  endHeight: number,
  onFinish: () => void
): void {
  const state = getState(details);

  const start = `${startHeight}px`;
  const end = `${endHeight}px`;
  details.style.overflow = "hidden";
  details.style.height = end;

  const animation = details.animate(
    {
      height: [start, end],
    },
    {
      duration: DURATION,
      easing: EASING,
    }
  );

  state.animation = animation;

  animation.onfinish = () => {
    if (state.animation !== animation) return;
    onFinish();
    clearAnimatedStyles(details);
    state.animation = null;
    resetAnimationFlags(details);
  };

  animation.oncancel = () => {
    if (state.animation !== animation) return;
    state.animation = null;
    details.style.height = `${getElementHeight(details)}px`;
    details.style.overflow = "hidden";
    resetAnimationFlags(details);
  };
}

function closeAccordion(details: HTMLDetailsElement, summary: HTMLElement): void {
  const state = getState(details);
  state.isClosing = true;
  state.isExpanding = false;

  cancelOngoingAnimation(details);

  const startHeight = getElementHeight(details);
  const endHeight = getElementHeight(summary);

  animateAccordionHeight(details, startHeight, endHeight, () => {
    details.open = false;
  });
}

function openAccordion(details: HTMLDetailsElement): void {
  const state = getState(details);
  state.isClosing = false;
  state.isExpanding = true;

  cancelOngoingAnimation(details);

  const startHeight = getElementHeight(details);
  details.open = true;

  // Measure fully expanded natural height and then animate from the current visible height.
  details.style.height = "auto";
  const endHeight = getElementHeight(details);

  animateAccordionHeight(details, startHeight, endHeight, () => {
    details.open = true;
  });
}

function onAccordionSummaryClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const summary = target.closest(ACCORDION_SUMMARY_SELECTOR);
  if (!(summary instanceof HTMLElement)) return;

  const details = summary.parentElement;
  if (!(details instanceof HTMLDetailsElement)) return;

  if (isReducedMotionPreferred()) {
    cancelOngoingAnimation(details);
    resetAnimationFlags(details);
    clearAnimatedStyles(details);
    return;
  }

  event.preventDefault();

  const state = getState(details);
  const shouldOpen = state.isClosing || !details.open;

  if (shouldOpen) {
    openAccordion(details);
  } else {
    closeAccordion(details, summary);
  }
}

export function initAccordion(): void {
  document.addEventListener("click", onAccordionSummaryClick);
}
